// The V6 point-of-care rules the console's Chart, Say it once and Resident trends use
// (design point-of-care.js: vitals, flag levels, meals, daily care), rules only. The phone
// keeps the same rules in apps/mobile/src/features/care/pointOfCare.ts. Residents always
// come from the server, never from here. The flag levels are Aithora's defaults until a
// building sets its own (the design says so on screen).

export type VitalKey = 'temp' | 'pulse' | 'resp' | 'sys' | 'dia' | 'spo2' | 'pain' | 'wt';
export interface VitalField {
  k: VitalKey;
  label: string;
  unit: string;
  flag: (v: number) => string;
}

export const VITALS: readonly VitalField[] = [
  { k: 'temp', label: 'Temperature', unit: '°F', flag: (v) => (v >= 100.4 ? 'Fever' : v < 96 ? 'Low temperature' : '') },
  { k: 'pulse', label: 'Pulse', unit: 'bpm', flag: (v) => (v > 110 ? 'Fast pulse' : v < 50 ? 'Slow pulse' : '') },
  { k: 'resp', label: 'Breathing', unit: '/min', flag: (v) => (v > 24 ? 'Fast breathing' : v < 10 ? 'Slow breathing' : '') },
  { k: 'sys', label: 'BP top number', unit: 'mmHg', flag: (v) => (v >= 160 ? 'High blood pressure' : v < 90 ? 'Low blood pressure' : '') },
  { k: 'dia', label: 'BP bottom number', unit: 'mmHg', flag: (v) => (v >= 100 ? 'High blood pressure' : '') },
  { k: 'spo2', label: 'Oxygen', unit: '%', flag: (v) => (v < 92 ? 'Low oxygen' : '') },
  { k: 'pain', label: 'Pain, 0 to 10', unit: '', flag: (v) => (v >= 7 ? 'Severe pain' : '') },
  { k: 'wt', label: 'Weight', unit: 'lb', flag: () => '' },
];

export type VitalValues = Partial<Record<VitalKey, string>>;

/** A typed number, or null when the field is blank or not a number. */
export const numOf = (raw: string | undefined): number | null => {
  const t = String(raw ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** The flag under one field, or '' when it is in range, blank or not a number. */
export function fieldFlag(f: VitalField, raw: string | undefined): string {
  const n = numOf(raw);
  return n === null ? '' : f.flag(n);
}

/** "Fever (101.2 °F)" for every reading out of range, in the form's order. */
export function vitalFlags(vals: VitalValues): string[] {
  const out: string[] = [];
  for (const f of VITALS) {
    const n = numOf(vals[f.k]);
    if (n === null) continue;
    const t = f.flag(n);
    if (t) out.push(`${t} (${n}${f.unit ? ` ${f.unit}` : ''})`);
  }
  return out;
}

/** "Temperature 98.6 °F · Pulse 52 bpm": the readings as they go on the chart. */
export function vitalsLine(vals: VitalValues): string {
  return VITALS.filter((f) => numOf(vals[f.k]) !== null).map((f) => `${f.label} ${numOf(vals[f.k])}${f.unit ? ` ${f.unit}` : ''}`).join(' · ');
}

export const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack'] as const;
export const EATEN = ['Refused', '25%', '50%', '75%', '100%'] as const;
export const ADLS = ['Bed mobility', 'Transfer', 'Walking', 'Toileting', 'Eating', 'Dressing', 'Hygiene'] as const;
export const LEVELS = ['Independent', 'Set-up help', 'Supervision', 'Some help', 'A lot of help', 'Did it for them', 'Did not happen'] as const;

/** "Dinner: ate 50%. Fluids +240 mL." */
export function mealLine(meal: string, eaten: string, ml: number): string {
  const ate = eaten ? (eaten === 'Refused' ? `${meal}: refused.` : `${meal}: ate ${eaten}.`) : `${meal}.`;
  return ml > 0 ? `${ate} Fluids +${ml} mL.` : ate;
}

/** "Daily care: Walking: Some help · Transfer: A lot of help". */
export function adlLine(adl: Record<string, string>): string {
  return `Daily care: ${ADLS.filter((a) => adl[a]).map((a) => `${a}: ${adl[a]}`).join(' · ')}`;
}

/** Who a point-of-care sheet can be about: from the server (the shift's assignment, or the building). */
export interface PocResident {
  id: string;
  name: string | null;
  room: string | null;
}

/** "Eleanor Marsh · 214": the full name, never cut short. */
export const residentLabel = (r: PocResident) => [r.name ?? 'Resident', r.room].filter(Boolean).join(' · ');
